/**
 * VisitorShortsPromo — Pastille promo flottante "SHORTS" pour visiteurs non connectés.
 *
 * Objectif : amplifier la découverte de la rubrique Shorts auprès des visiteurs
 * (c'est l'outil que l'utilisateur anticipe comme le plus utilisé).
 *
 * Règles d'affichage :
 *   - UNIQUEMENT si pas d'utilisateur connecté (useApp().user === null)
 *   - UNIQUEMENT sur les pages piliers (Home, Magazine, Vidéos, Annuaire, Code, Épreuves)
 *   - UNIQUEMENT si l'utilisateur ne l'a pas fermée dans la session courante
 *   - UNIQUEMENT après 1,5 s de scroll ou présence sur la page (pas d'intrusion dès l'arrivée)
 *   - Masquée automatiquement sur /shorts et /shorts/*
 *
 * UX : pilule rose bottom-left desktop, bottom-left juste au-dessus de la BottomNav sur mobile.
 * Dismissible via bouton X (persistée en sessionStorage).
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Film, X } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const PILLAR_MATCHERS = [
  (p) => p === '/',
  (p) => p === '/magazine' || p.startsWith('/magazine'),
  (p) => p === '/videos' || p === '/tutos-video' || p === '/ressources-officielles',
  (p) => p === '/moto-ecoles' || p.startsWith('/moto-ecoles'),
  (p) => p.startsWith('/code-de-la-route'),
  (p) => p.startsWith('/epreuves/'),
  (p) => p === '/bibliotheque' || p.startsWith('/bibliotheque'),
];

const EXCLUDE = (p) => p.startsWith('/shorts') || p.startsWith('/admin') || p.startsWith('/eleve') || p.startsWith('/moniteur') || p.startsWith('/enseignant');

const STORAGE_KEY = 'c2r:visitor-shorts-promo:dismissed';

const isPillarPage = (pathname) => {
  if (EXCLUDE(pathname)) return false;
  return PILLAR_MATCHERS.some((fn) => fn(pathname));
};

export default function VisitorShortsPromo() {
  const { pathname } = useLocation();
  const { user } = useApp() || {};
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Charge l'état "fermé par l'utilisateur" une seule fois à l'arrivée
  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(STORAGE_KEY) === '1');
    } catch (_) { /* silent */ }
  }, []);

  // Montre la promo après 1,5 s, uniquement si visiteur et page pilier
  useEffect(() => {
    setVisible(false);
    if (user || dismissed || !isPillarPage(pathname)) return undefined;
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [pathname, user, dismissed]);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) { /* silent */ }
    setDismissed(true);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside
      // Bottom-left pour ne jamais chevaucher le BackToTop (bottom-right) ni le CoachChatWidget.
      // Sur mobile on remonte au-dessus de la BottomNav (72 px) + safe-area.
      className="fixed left-3 sm:left-4 z-30 pointer-events-none"
      style={{
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 86px)',
      }}
      data-testid="visitor-shorts-promo"
    >
      <div className="pointer-events-auto animate-[slideUp_.4s_ease-out]">
        <Link
          to="/shorts"
          className="group relative inline-flex items-center gap-2 pl-2.5 pr-8 py-2 rounded-full bg-gradient-to-r from-red-600 to-rose-500 text-white font-black uppercase tracking-wider text-[11px] sm:text-[12px] shadow-xl shadow-red-900/40 ring-1 ring-white/15 hover:scale-[1.03] active:scale-95 transition-transform"
          aria-label="Découvrir les Shorts moto — Nouveau"
          data-testid="visitor-shorts-promo-cta"
        >
          <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-white/15 shrink-0">
            <Film className="w-3.5 h-3.5" aria-hidden="true" />
            <span aria-hidden="true" className="absolute inset-0 rounded-full">
              <span className="absolute inset-0 rounded-full bg-white/25 animate-ping" />
            </span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[9.5px] font-bold tracking-[0.14em] text-white/85">NOUVEAU</span>
            <span className="text-[12px] sm:text-[13px] font-black">Shorts moto · 60s</span>
          </span>
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 text-white grid place-items-center transition-colors pointer-events-auto"
          aria-label="Fermer la promo Shorts"
          data-testid="visitor-shorts-promo-dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="visitor-shorts-promo"] * { animation: none !important; }
        }
      `}</style>
    </aside>
  );
}
